import { Button } from 'components/button/Button'
import { SDot, SDotsContainer } from 'components/dots/DotsContainer'
import { Picture } from 'components/picture/Picture'
import { SquooshedPictureKey } from 'components/picture/pictures'
import { Stack } from 'components/Stack'
import { Text, Title } from 'components/Typography'
import { AnimatePresence, motion } from 'framer-motion'
import { useTranslation } from 'next-i18next'
import { useState } from 'react'
import { logError } from 'utils/general'
import { SOnboardingModal, SOnboardingModalIllustration } from './OnboardingModal.styled'

export type TOnboardingModalSteps = {
  illustration: { src: string; srcSet?: string } | { imageKey: SquooshedPictureKey }
  title: string
  description: string
  buttonLabel?: string
}

const ANIM_PROPS = {
  initial: { opacity: 0, x: 50 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -50 },
}

export const OnboardingModal = (props: {
  title: string
  steps: TOnboardingModalSteps[]
  onNextClick?: () => void
  onSuccess: () => void
}) => {
  const { t } = useTranslation()
  const [step, setStep] = useState(0)

  const stepData = props.steps?.[step]

  const onButtonAction = () => {
    if (step < props.steps.length - 1) {
      props.onNextClick?.()
      setStep((step) => ++step)
    } else {
      props.onSuccess()
    }
  }

  const onDotClick = (index: number) => {
    setStep(index)
  }

  if (!stepData) {
    logError('Missing data')
    return null
  } else {
    return (
      <SOnboardingModal>
        <Text variant="bodyBold">{props.title}</Text>

        <AnimatePresence exitBeforeEnter>
          {'imageKey' in stepData.illustration ? (
            <div
              css={
                stepData.illustration.imageKey === 'illustrations/family'
                  ? { padding: '2rem 0' }
                  : undefined
              }
            >
              <Picture
                imageKey={stepData.illustration.imageKey}
                motionImageComponent={SOnboardingModalIllustration}
                motionImageProps={{ ...ANIM_PROPS, key: step }}
                key={step}
              />
            </div>
          ) : (
            <SOnboardingModalIllustration
              alt=""
              key={step}
              src={stepData.illustration.src}
              srcSet={stepData.illustration?.srcSet}
              {...ANIM_PROPS}
            />
          )}
        </AnimatePresence>

        <Stack space="1.5rem">
          <AnimatePresence exitBeforeEnter>
            <motion.div key={step} {...ANIM_PROPS}>
              <Title variant={'h7'} as="h6">
                {stepData.title}
              </Title>

              <Text>{stepData.description}</Text>
            </motion.div>
          </AnimatePresence>

          <SDotsContainer className="navigator">
            {props.steps?.map((_, i) => (
              <SDot key={i} isActive={i === step} onClick={() => onDotClick(i)} />
            ))}
          </SDotsContainer>

          <Button type="button" block onClick={onButtonAction}>
            {stepData.buttonLabel || t('common.next')}
          </Button>
        </Stack>
      </SOnboardingModal>
    )
  }
}
